import { useMutation, useQuery, useQueryClient } from 'react-query';
import adminUsersService, {
  AdminUserRow,
  CreateAdminUserBody,
  UpdateAdminUserBody,
} from '../services/adminUsers.service';

export type { AdminUserRow, CreateAdminUserBody, UpdateAdminUserBody };

const ADMIN_USERS_QUERY_KEY = ['admin-users'] as const;

const useAdminUsers = (enabled = true) =>
  useQuery<{ items: AdminUserRow[] }>(ADMIN_USERS_QUERY_KEY, () => adminUsersService.list(), {
    refetchOnWindowFocus: false,
    enabled,
  });

export const useCreateAdminUser = () => {
  const queryClient = useQueryClient();

  return useMutation<AdminUserRow, unknown, CreateAdminUserBody>(
    body => adminUsersService.create(body),
    { onSuccess: () => queryClient.invalidateQueries(ADMIN_USERS_QUERY_KEY) }
  );
};

export const useUpdateAdminUser = () => {
  const queryClient = useQueryClient();

  return useMutation<AdminUserRow, unknown, { id: number; body: UpdateAdminUserBody }>(
    ({ id, body }) => adminUsersService.update(id, body),
    {
      onSuccess: () => queryClient.invalidateQueries(ADMIN_USERS_QUERY_KEY),
    }
  );
};

export const useDeleteAdminUser = () => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, number>(id => adminUsersService.remove(id), {
    onSuccess: () => queryClient.invalidateQueries(ADMIN_USERS_QUERY_KEY),
  });
};

export const useResetAdminUserPassword = () =>
  useMutation<void, unknown, number>(id => adminUsersService.resetPassword(id));

export const useResetAdminUserMfa = () => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, number>(id => adminUsersService.resetMfa(id), {
    onSuccess: () => queryClient.invalidateQueries(ADMIN_USERS_QUERY_KEY),
  });
};

export default useAdminUsers;
